import type {
    CompensationPayoutForServiceActivityUpdatableFields,
    CompensationPayoutForServiceActivityWithId,
    CompensationPayoutWithId,
} from '@common/validation/compensationPayoutForServiceActivity';
import { fetchJson } from '@frontend/store/fetch';

const baseUrl = '/api/finance/compensationPayouts';

export async function getServiceActivityCompensationPayouts(
    params: {
        serviceActivityId?: string;
        serviceGroupSettingId?: string;
        serviceId?: string;
        serviceOrganizationSettingId?: string;
    },
    signal: AbortSignal
): Promise<CompensationPayoutForServiceActivityWithId[]> {
    const { serviceGroupSettingId, serviceOrganizationSettingId, serviceActivityId, serviceId } = params;

    const queryParams = new URLSearchParams({
        ...(serviceGroupSettingId ? { serviceGroupSettingId } : {}),
        ...(serviceOrganizationSettingId ? { serviceOrganizationSettingId } : {}),
        ...(serviceActivityId ? { serviceActivityId } : {}),
        ...(serviceId ? { serviceId } : {}),
    });

    const url = `${baseUrl}?${queryParams.toString()}`;
    const response = await fetchJson({ method: 'GET', signal, url });

    return response.json.items ?? [];
}

export async function getOneServiceActivityCompensationPayout(
    id: string,
    signal: AbortSignal
): Promise<CompensationPayoutForServiceActivityWithId> {
    const url = `${baseUrl}/${id}`;
    const response = await fetchJson({ method: 'GET', signal, url });

    return response.json;
}

export async function updateAll(
    params: {
        compensationPayouts: CompensationPayoutWithId[];
        serviceActivityId: string;
        serviceEmploymentId?: string;
        serviceOrganizationSettingId?: string;
    },
    signal: AbortSignal
) {
    const { compensationPayouts, serviceActivityId, serviceEmploymentId, serviceOrganizationSettingId } = params;

    const body = {
        compensationPayouts,
        serviceActivityId,
    };

    const queryParams = new URLSearchParams({
        ...(serviceEmploymentId ? { serviceEmploymentId } : {}),
        ...(serviceOrganizationSettingId ? { serviceOrganizationSettingId } : {}),
    });

    const url = `${baseUrl}/updateAll?${queryParams.toString()}`;

    await fetchJson({ body, method: 'POST', signal, url });
}

export async function createServiceActivityCompensationPayout(
    compensationPayout: CompensationPayoutForServiceActivityUpdatableFields,
    settingId: {
        serviceGroupSettingId?: string;
        serviceId?: string;
        serviceOrganizationSettingId?: string;
    },
    signal: AbortSignal
) {
    if (Object.values(settingId).filter((id) => !!id).length > 1) {
        throw new Error('Only one settingId may be filled in at the same time');
    }

    const body = {
        ...compensationPayout,
        serviceEmploymentId: null,
        serviceGroupSettingId: settingId.serviceGroupSettingId ?? null,
        serviceId: settingId.serviceId ?? null,
        serviceOrganizationSettingId: settingId.serviceOrganizationSettingId ?? null,
    };

    await fetchJson({ body, method: 'POST', signal, url: baseUrl });
}

export async function updateOneServiceActivityCompensationPayout(
    compensationPayout: CompensationPayoutForServiceActivityUpdatableFields,
    id: string,
    signal: AbortSignal
) {
    const url = `${baseUrl}/${id}`;

    await fetchJson({ body: compensationPayout, method: 'POST', signal, url });
}

export async function deleteServiceActivityCompensationPayouts(id: string, signal: AbortSignal) {
    const url = `${baseUrl}/${id}`;

    await fetchJson({ method: 'DELETE', signal, url });
}
